import React, { useState, useRef, useEffect } from 'react';

interface CommentInputProps {
  position: { x: number; y: number };
  onSubmit: (text: string) => void;
  onCancel: () => void;
}

/**
 * Input dialog for creating a new comment.
 * Appears when user clicks in comment mode.
 */
const CommentInput: React.FC<CommentInputProps> = ({
  position,
  onSubmit,
  onCancel,
}) => {
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Auto-focus textarea
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);
  
  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onCancel();
      }
    };
    
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onCancel]);
  
  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      if (text.trim()) {
        onSubmit(text.trim());
      }
    }
  };
  
  const handleSubmit = () => {
    if (text.trim()) {
      onSubmit(text.trim());
    }
  };
  
  // Calculate position to ensure dialog stays within viewport
  const dialogWidth = 280;
  const dialogHeight = 220;
  const padding = 20;
  
  let left = position.x + 20;
  let top = position.y - 20;
  
  // Adjust if going off right edge
  if (left + dialogWidth > window.innerWidth - padding) {
    left = position.x - dialogWidth - 20;
  }
  // Adjust if going off bottom
  if (top + dialogHeight > window.innerHeight - padding) {
    top = window.innerHeight - dialogHeight - padding;
  }
  // Adjust if going off top
  if (top < padding) {
    top = padding;
  }
  
  return (
    <>
      {/* Marker at click position */}
      <div
        style={{
          position: 'fixed',
          left: position.x - 8,
          top: position.y - 8,
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: '#f59e0b',
          border: '2px solid white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          zIndex: 1001,
          pointerEvents: 'none',
        }}
      />
      <div
        ref={containerRef}
        style={{
          position: 'fixed',
          left,
          top,
          zIndex: 1000,
          width: dialogWidth,
          background: 'white',
          borderRadius: 10,
          boxShadow: '0 8px 32px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.05)',
          overflow: 'hidden',
        }}
      >
      {/* Header */}
      <div
        style={{
          padding: '10px 12px',
          borderBottom: '1px solid #e5e7eb',
          background: '#fffbeb',
          fontSize: 13,
          fontWeight: 600,
          color: '#92400e',
        }}
      >
        Add Comment
      </div>
      
      {/* Textarea */}
      <div style={{ padding: '12px' }}>
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Write your comment..."
          style={{
            width: '100%',
            minHeight: 80,
            padding: '8px 10px',
            fontSize: 14,
            border: '1px solid #d1d5db',
            borderRadius: 6,
            resize: 'vertical',
            outline: 'none',
            fontFamily: 'inherit',
          }}
        />
        <div style={{ marginTop: 4, fontSize: 11, color: '#9ca3af' }}>
          Press ⌘+Enter to submit
        </div>
      </div>
      
      {/* Actions */}
      <div
        style={{
          padding: '8px 12px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
        }}
      >
        <button
          onClick={onCancel}
          style={{
            padding: '6px 12px',
            fontSize: 12,
            fontWeight: 500,
            borderRadius: 6,
            border: '1px solid #d1d5db',
            background: 'white',
            color: '#374151',
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={!text.trim()}
          style={{
            padding: '6px 12px',
            fontSize: 12,
            fontWeight: 500,
            borderRadius: 6,
            border: 'none',
            background: text.trim() ? '#f59e0b' : '#e5e7eb',
            color: text.trim() ? 'white' : '#9ca3af',
            cursor: text.trim() ? 'pointer' : 'not-allowed',
          }}
        >
          Add Comment
        </button>
      </div>
      </div>
    </>
  );
};

export default CommentInput;
